import { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Sparkles } from 'lucide-react';
import { apiFetch, parseApiError } from '../../api/client';
import { useTheme } from '../../context/ThemeContext';
import { useTenantView } from '../../context/TenantViewContext';
import { useAuth } from '../../context/AuthContext';
import { withAlpha } from '../../lib/chartColors';

const INITIAL_MESSAGES = [
  {
    id: 1,
    role: 'ai',
    text: 'Ask me about revenue, invoice counts, top products, or top venues.',
  },
];

function buildChatHistory(messages) {
  return messages
    .filter((message) => message.role === 'user' || message.role === 'ai')
    .slice(-10)
    .map((message) => ({
      role: message.role,
      text: message.text,
    }));
}

async function fetchChatResponse(question, messages, accountId) {
  try {
    const body = { question };
    if (accountId != null) {
      body.accountId = accountId;
    }
    const res = await apiFetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, history: buildChatHistory(messages) }),
    });
    if (res.status === 401) {
      return 'Your session has expired. Please sign in again.';
    }
    if (!res.ok) {
      const message = await parseApiError(res, 'AI assistant is temporarily unavailable.');
      return message;
    }
    const data = await res.json();
    return data.answer || 'AI is coming soon. Stay tuned!';
  } catch {
    return 'Unable to reach the AI assistant right now. Please try again.';
  }
}

function lastUserQuestion(messages) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'user') return messages[i].text;
  }
  return '';
}

function expandFollowUpQuestion(question, messages) {
  const normalized = question.toLowerCase().replace(/[?.!]/g, '').trim();
  const monthNames = 'january|jan|february|feb|march|mar|april|apr|may|june|jun|july|jul|august|aug|september|sep|sept|october|oct|november|nov|december|dec';
  const dateOnly =
    normalized.match(/^(for\s+|in\s+)?(today|yesterday|this week|last week|this month|last month)$/) ||
    normalized.match(new RegExp(`^(for\\s+|in\\s+)?(${monthNames})(\\s+20\\d{2})?$`));
  if (!dateOnly) return question;

  const period = `${dateOnly[2]}${dateOnly[3] || ''}`;
  const previous = lastUserQuestion(messages).toLowerCase();

  if (previous.includes('order') || previous.includes('invoice')) {
    return `How many orders were there ${period}?`;
  }
  if (previous.includes('top product') || previous.includes('best product')) {
    return `What was the top product ${period}?`;
  }
  if (previous.includes('top venue') || previous.includes('best venue')) {
    return `What was the top venue ${period}?`;
  }
  return `What was total revenue ${period}?`;
}

function ChatMessages({ messages, loading, bottomRef, primaryColor }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 py-4">
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[80%] whitespace-pre-line break-words px-3.5 py-2.5 text-[13px] leading-relaxed ${
              msg.role === 'user'
                ? 'rounded-2xl rounded-br-sm text-white'
                : 'rounded-2xl rounded-bl-sm bg-slate-100 text-slate-800'
            }`}
            style={msg.role === 'user' ? { backgroundColor: primaryColor } : undefined}
          >
            {msg.text}
          </div>
        </div>
      ))}

      {loading && (
        <div className="flex justify-start">
          <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-slate-100 px-3.5 py-3">
            {[0, 1, 2].map((index) => (
              <span
                key={index}
                className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400"
                style={{
                  animation: 'vi-bounce 1.2s infinite',
                  animationDelay: `${index * 0.2}s`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}

function ChatInput({ input, loading, inputRef, onChange, onKeyDown, onSend, primaryColor }) {
  return (
    <div className="flex flex-shrink-0 items-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
      <textarea
        ref={inputRef}
        value={input}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder="Ask a revenue question..."
        rows={1}
        className="max-h-24 min-h-[42px] flex-1 resize-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[13px] text-slate-800 outline-none transition-colors focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/15"
      />
      <button
        type="button"
        onClick={onSend}
        disabled={!input.trim() || loading}
        aria-label="Send message"
        className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-lg text-white transition duration-150 hover:scale-105 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:hover:scale-100"
        style={!input.trim() || loading ? undefined : { backgroundColor: primaryColor }}
      >
        <Send className="h-4 w-4" />
      </button>
    </div>
  );
}

function ChatHeader({ embedded, onClose, primaryColor }) {
  return (
    <div
      className="flex flex-shrink-0 items-center gap-2 px-4 py-3.5"
      style={{
        backgroundColor: primaryColor,
        borderBottom: `1px solid ${withAlpha(primaryColor, 0.2)}`,
      }}
    >
      <Sparkles className="h-4 w-4 text-white/85" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-white">Ask about your revenue</div>
        <div className="text-[11px] text-white/60">Powered by Venue Intelligence AI</div>
      </div>
      {!embedded && onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close chat"
          className="flex items-center rounded p-1 text-white/70 transition-colors hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

function ChatSurface({
  embedded,
  open,
  onClose,
  messages,
  loading,
  input,
  inputRef,
  bottomRef,
  onInputChange,
  onKeyDown,
  onSend,
  primaryColor,
}) {
  const surfaceClass = embedded
    ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.08)]'
    : `fixed bottom-20 left-4 right-4 z-[49] mx-auto flex max-h-[min(480px,calc(100vh-7rem))] w-auto max-w-[340px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)] transition-all duration-200 ease-out sm:bottom-[84px] sm:left-auto sm:right-6 sm:mx-0 sm:w-[340px] ${
        open ? 'pointer-events-auto translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-3 scale-[0.97] opacity-0'
      }`;

  if (!embedded && !open) {
    return null;
  }

  return (
    <div className={surfaceClass} role={embedded ? 'region' : 'dialog'} aria-label="AI chat">
      <ChatHeader embedded={embedded} onClose={onClose} primaryColor={primaryColor} />
      <ChatMessages messages={messages} loading={loading} bottomRef={bottomRef} primaryColor={primaryColor} />
      <ChatInput
        input={input}
        loading={loading}
        inputRef={inputRef}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onSend={onSend}
        primaryColor={primaryColor}
      />
    </div>
  );
}

export function AiChatPanel({ embedded = false, open: openProp, onOpenChange }) {
  const { primaryColor } = useTheme();
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const scopedAccountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = embedded ? true : (isControlled ? openProp : internalOpen);

  function setOpen(nextOpen) {
    if (embedded) return;
    if (isControlled) {
      onOpenChange?.(nextOpen);
      return;
    }
    setInternalOpen(nextOpen);
  }

  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  async function handleSend() {
    const question = input.trim();
    if (!question || loading) return;

    const userMsg = { id: Date.now(), role: 'user', text: question };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const questionForApi = expandFollowUpQuestion(question, messages);
    const answer = await fetchChatResponse(questionForApi, messages, scopedAccountId);
    const aiMsg = { id: Date.now() + 1, role: 'ai', text: answer };
    setMessages((prev) => [...prev, aiMsg]);
    setLoading(false);
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }

  const chatSurface = (
    <ChatSurface
      embedded={embedded}
      open={open}
      onClose={() => setOpen(false)}
      messages={messages}
      loading={loading}
      input={input}
      inputRef={inputRef}
      bottomRef={bottomRef}
      onInputChange={(event) => setInput(event.target.value)}
      onKeyDown={handleKeyDown}
      onSend={handleSend}
      primaryColor={primaryColor}
    />
  );

  return (
    <>
      {!embedded && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Close AI chat' : 'Open AI chat'}
          className="fixed bottom-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-xl text-white transition-all hover:scale-105 sm:bottom-6 sm:right-6"
          style={{
            backgroundColor: primaryColor,
            boxShadow: `0 4px 16px ${withAlpha(primaryColor, 0.28)}`,
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.boxShadow = `0 6px 20px ${withAlpha(primaryColor, 0.38)}`;
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.boxShadow = `0 4px 16px ${withAlpha(primaryColor, 0.28)}`;
          }}
        >
          {open ? <X className="h-[18px] w-[18px]" /> : <MessageSquare className="h-[18px] w-[18px]" />}
        </button>
      )}

      {embedded ? (
        <div className="flex min-h-0 flex-1 flex-col">{chatSurface}</div>
      ) : (
        chatSurface
      )}

      <style>{`
        @keyframes vi-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}
